import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProjectTrackerTab from "../shared/ProjectTrackerTab";
import { 
  LogOut, 
  User, 
  Users,
  Search,
  RefreshCw,
  Loader2,
  Activity,
  Globe,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_FLAGS = {
  usa: '🇺🇸',
  canada: '🇨🇦',
  jamaica: '🇯🇲',
  barbados: '🇧🇧',
  trinidad_tobago: '🇹🇹',
  panama: '🇵🇦'
};

const REGION_NAMES = {
  usa: 'USA',
  canada: 'Canada',
  jamaica: 'Jamaica',
  barbados: 'Barbados',
  trinidad_tobago: 'Trinidad & Tobago',
  panama: 'Panama'
};

export default function ProjectManagerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState({ total: 0, completed: 0, started: 0, pending: 0, typeStats: [] });
  const [employees, setEmployees] = useState([]);
  const [employeesTotal, setEmployeesTotal] = useState(0);
  const [regionStats, setRegionStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("fullName");
  const [sortOrder, setSortOrder] = useState("asc");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("projectManagerToken") || localStorage.getItem("authToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Fetch dashboard stats and employees from executive endpoints
      const [statsRes, employeesRes, activityRes] = await Promise.all([
        axios.get(`${API}/executive/dashboard-stats`, getAuthHeaders()),
        axios.get(`${API}/executive/employees?limit=1000`, getAuthHeaders()),
        axios.get(`${API}/executive/live-activity`, getAuthHeaders())
      ]);

      const statsData = statsRes.data;
      setStats({
        total: statsData.total || 0,
        completed: statsData.completed || 0,
        started: statsData.started || 0,
        pending: statsData.pending || 0,
        typeStats: statsData.typeStats || []
      });

      // Build region stats from the stats response
      if (statsData.regionStats) {
        setRegionStats(statsData.regionStats.map(r => ({
          code: r.code,
          name: r.name,
          flag: r.flag,
          total: r.total || 0,
          completed: r.completed || 0,
          started: r.total - r.completed || 0,
          pending: r.pending || 0,
          completionRate: r.completionRate || 0
        })));
      }

      // Set employees
      const employeeList = employeesRes.data.employees || [];
      setEmployees(employeeList);
      setEmployeesTotal(employeesRes.data.total || employeeList.length);

      // Set activity
      if (activityRes.data.activity) {
        setRecentActivity(activityRes.data.activity.slice(0, 10));
      }

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("projectManagerToken") || localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const userData = await response.json();
          if (userData.role !== 'project_manager') {
            toast.error("Access denied. Project Manager role required.");
            navigate("/login");
            return;
          }
          setUser(userData);
          fetchDashboardData();
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [navigate, fetchDashboardData]);

  const handleLogout = () => {
    localStorage.removeItem("projectManagerToken");
    localStorage.removeItem("authToken");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "just now";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Filter and sort employees
  const filteredEmployees = employees
    .filter(emp => {
      if (statusFilter !== "all") {
        if (statusFilter === "completed" && emp.status !== "completed") return false;
        if (statusFilter === "started" && emp.status !== "started") return false;
        if (statusFilter === "pending" && emp.status !== "pending") return false;
      }
      if (typeFilter !== "all") {
        if (typeFilter === "self_assessment" && emp.assessmentType !== "self_assessment") return false;
        if (typeFilter === "manager_validation" && emp.assessmentType !== "manager_validation") return false;
      }
      if (segmentFilter !== "all" && emp.segment !== segmentFilter) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const name = (emp.fullName || emp.name || '').toLowerCase();
        const email = (emp.email || '').toLowerCase();
        const region = (emp.region || '').toLowerCase();
        if (!name.includes(search) && !email.includes(search) && !region.includes(search)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (sortOrder === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#63C465]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="pm-dashboard">
      {/* Header - Matching Executive dashboard style */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1600px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#63C465] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="text-lg font-semibold text-[#3b3b3b] hidden sm:inline">
                Workforce Intelligence
              </span>
            </div>

            {/* Center - Search */}
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
                <Input
                  placeholder="Search by name, role, region..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#f8f9fa] border-[#e5e7eb] text-[#3b3b3b] placeholder:text-[#9ca3af] focus:border-[#00AEEF] h-9"
                  data-testid="pm-search"
                />
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#4b5563] hover:text-[#3b3b3b]"
                onClick={fetchDashboardData}
                disabled={refreshing}
                data-testid="pm-refresh"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <div className="flex items-center gap-2 text-sm text-[#4b5563]">
                <User className="w-4 h-4" />
                <span data-testid="pm-user-name">{user?.fullName || user?.full_name || 'Unri Babb'}</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-[#4b5563] hover:text-[#3b3b3b]"
                onClick={handleLogout}
                data-testid="pm-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1600px] mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 gap-1">
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] rounded-lg px-4 text-[13px] font-medium"
                data-testid="tab-overview"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="project-tracker" 
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] rounded-lg px-4 text-[13px] font-medium"
                data-testid="tab-project-tracker"
              >
                Project Tracker
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Self Assessment */}
              {(() => {
                const saStats = stats.typeStats.find(t => t.code === 'self_assessment');
                const saTotal = saStats?.total || 0;
                const saCompleted = saStats?.completed || 0;
                const saRate = saTotal > 0 ? Math.round((saCompleted / saTotal) * 100) : 0;
                return (
                  <Card className="bg-white shadow-sm border-l-4 border-l-[#00AEEF]">
                    <CardContent className="pt-4">
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Self Assessment
                      </p>
                      <p className="text-4xl font-bold text-[#00AEEF]" data-testid="stat-sa-total">
                        {saTotal}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{saCompleted} completed · {saRate}%</p>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Manager Validation */}
              {(() => {
                const mvStats = stats.typeStats.find(t => t.code === 'manager_validation');
                const mvTotal = mvStats?.total || 0;
                const mvCompleted = mvStats?.completed || 0;
                const mvRate = mvTotal > 0 ? Math.round((mvCompleted / mvTotal) * 100) : 0;
                return (
                  <Card className="bg-white shadow-sm border-l-4 border-l-[#8B5CF6]">
                    <CardContent className="pt-4">
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Manager Validation
                      </p>
                      <p className="text-4xl font-bold text-[#8B5CF6]" data-testid="stat-mv-total">
                        {mvTotal}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{mvCompleted} completed · {mvRate}%</p>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Completed */}
              <Card className="bg-white shadow-sm border-l-4 border-l-emerald-500">
                <CardContent className="pt-4">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Completed
                  </p>
                  <p className="text-4xl font-bold text-emerald-600" data-testid="stat-completed">
                    {stats.completed}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">All assessments done</p>
                </CardContent>
              </Card>

              {/* In Progress */}
              <Card className="bg-white shadow-sm border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    In Progress
                  </p>
                  <p className="text-4xl font-bold text-blue-600" data-testid="stat-in-progress">
                    {stats.started}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Currently active</p>
                </CardContent>
              </Card>

              {/* Pending / Not Started */}
              <Card className="bg-white shadow-sm border-l-4 border-l-slate-400">
                <CardContent className="pt-4">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Pending
                  </p>
                  <p className="text-4xl font-bold text-slate-600" data-testid="stat-pending">
                    {stats.pending || (stats.total - stats.completed - stats.started)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Not started yet</p>
                </CardContent>
              </Card>

              {/* Completion Rate */}
              <Card className="bg-white shadow-sm border-l-4 border-l-amber-500">
                <CardContent className="pt-4">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    Completion Rate
                  </p>
                  <p className="text-4xl font-bold text-amber-600" data-testid="stat-completion-rate">
                    {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Of all assessments</p>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Grid - Employee Table + Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Column - Employee Table (3/4 width) */}
              <div className="lg:col-span-3 space-y-4">
                {/* Status Filter Tabs + Count */}
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                          All Assessments
                        </CardTitle>
                        <Badge variant="secondary" className="text-xs" data-testid="employee-count">{filteredEmployees.length}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                          <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="type-filter">
                            <SelectValue placeholder="All Types" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="self_assessment">Self Assessment</SelectItem>
                            <SelectItem value="manager_validation">Manager Validation</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                          <SelectTrigger className="w-[150px] h-8 text-xs">
                            <SelectValue placeholder="All Segments" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Segments</SelectItem>
                            <SelectItem value="Core Builder">Core Builder</SelectItem>
                            <SelectItem value="Emerging Builder">Emerging Builder</SelectItem>
                            <SelectItem value="Support Developer">Support Developer</SelectItem>
                            <SelectItem value="Transition Candidate">Transition Candidate</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* Status Tabs */}
                    <div className="flex gap-1 mt-3">
                      {['all', 'completed', 'started', 'pending'].map((status) => (
                        <Button
                          key={status}
                          variant={statusFilter === status ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setStatusFilter(status)}
                          className={`text-xs h-7 ${statusFilter === status ? 'bg-[#1a3a3a]' : ''}`}
                          data-testid={`filter-${status}`}
                        >
                          {status === 'all' ? 'All' : 
                           status === 'completed' ? 'Completed' :
                           status === 'started' ? 'In Progress' : 'Not Started'}
                        </Button>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Employee Table */}
                    <ScrollArea className="h-[500px]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white z-10">
                          <tr className="border-b">
                            <th 
                              className="text-left py-2 px-2 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                              onClick={() => handleSort("fullName")}
                            >
                              <div className="flex items-center gap-1">
                                Name
                                {sortBy === "fullName" && (sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                              </div>
                            </th>
                            <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden lg:table-cell">Email</th>
                            <th className="text-left py-2 px-2 font-medium text-muted-foreground">Manager</th>
                            <th className="text-left py-2 px-2 font-medium text-muted-foreground">Region</th>
                            <th className="text-left py-2 px-2 font-medium text-muted-foreground">Type</th>
                            <th className="text-center py-2 px-2 font-medium text-muted-foreground">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEmployees.map((emp, idx) => (
                            <tr 
                              key={emp.id || idx} 
                              className="border-b hover:bg-slate-50"
                              data-testid={`employee-row-${idx}`}
                            >
                              <td className="py-2 px-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-600">
                                    {(emp.fullName || emp.name || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-[#3b3b3b] truncate max-w-[150px]">
                                      {emp.fullName || emp.name}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground truncate max-w-[150px]">
                                      {emp.jobTitle || emp.job_title || '—'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-2 px-2 text-xs text-muted-foreground hidden lg:table-cell truncate max-w-[180px]">
                                {emp.email || '—'}
                              </td>
                              <td className="py-2 px-2 text-xs text-[#3b3b3b]">
                                {emp.managerName || emp.manager_name || '—'}
                              </td>
                              <td className="py-2 px-2">
                                <span className="text-lg mr-1">{REGION_FLAGS[emp.region] || '🌍'}</span>
                                <span className="text-xs text-muted-foreground uppercase">{emp.region || '—'}</span>
                              </td>
                              <td className="py-2 px-2 text-xs">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                  emp.assessmentType === 'manager_validation' 
                                    ? 'bg-purple-50 text-purple-700 border border-purple-200' 
                                    : 'bg-sky-50 text-sky-700 border border-sky-200'
                                }`}>
                                  {emp.assessmentType === 'manager_validation' ? 'MV' : 'SA'}
                                  <span className="hidden xl:inline">
                                    {emp.assessmentType === 'manager_validation' ? ' · Manager Validation' : ' · Self Assessment'}
                                  </span>
                                </span>
                              </td>
                              <td className="py-2 px-2 text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                  emp.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                  emp.status === 'started' ? 'bg-blue-50 text-blue-700' :
                                  'bg-slate-100 text-slate-500'
                                }`}>
                                  {emp.status === 'completed' ? 'Complete' :
                                   emp.status === 'started' ? 'In Progress' : 
                                   'Not Started'}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {filteredEmployees.length === 0 && (
                            <tr>
                              <td colSpan={6} className="py-8 text-center text-muted-foreground">
                                No employees found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Sidebar (1/4 width) */}
              <div className="space-y-6">
                {/* Live Activity */}
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-emerald-500" />
                      <CardTitle className="text-sm font-semibold text-[#3b3b3b]">Live Activity</CardTitle>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[200px]">
                      <div className="space-y-2">
                        {recentActivity.length > 0 ? recentActivity.map((activity, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-2 rounded bg-slate-50" data-testid={`activity-${idx}`}>
                            <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5">
                              <Activity className="w-3 h-3 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-[#3b3b3b] truncate">
                                {activity.userName || activity.name} <span className="font-normal text-muted-foreground">started</span>
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {REGION_FLAGS[activity.region] || '🌍'} · {activity.assessmentType === 'manager_validation' ? 'Manager Validation' : 'Self Assessment'} · {formatTimeAgo(activity.timestamp)}
                              </p>
                            </div>
                          </div>
                        )) : (
                          <p className="text-xs text-muted-foreground text-center py-4">No recent activity</p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Performance by Region */}
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#00AEEF]" />
                      <CardTitle className="text-sm font-semibold text-[#3b3b3b]">Performance by Region</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1.5 font-medium text-muted-foreground">REGION</th>
                            <th className="text-center py-1.5 font-medium text-muted-foreground">TOTAL</th>
                            <th className="text-center py-1.5 font-medium text-muted-foreground">DONE</th>
                            <th className="text-center py-1.5 font-medium text-muted-foreground">ACTIVE</th>
                            <th className="text-center py-1.5 font-medium text-muted-foreground">RATE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {regionStats.map((region) => (
                            <tr key={region.code} className="border-b last:border-0" data-testid={`region-${region.code}`}>
                              <td className="py-1.5">
                                <span className="mr-1">{region.flag}</span>
                                <span>{region.name}</span>
                              </td>
                              <td className="py-1.5 text-center font-medium">{region.total}</td>
                              <td className="py-1.5 text-center text-emerald-600 font-medium">{region.completed}</td>
                              <td className="py-1.5 text-center text-blue-600 font-medium">{region.started}</td>
                              <td className={`py-1.5 text-center font-medium ${
                                region.completionRate > 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                                {region.completionRate}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Project Tracker Tab */}
          <TabsContent value="project-tracker">
            <ProjectTrackerTab userRole="project_manager" />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
